// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { DoubleRightOutlined } from '@ant-design/icons';
import { Pagination, Spin } from 'antd';
import Body from './Body/Body';
import ColumnsHeader from './ColumnsHeader/ColumnsHeader';
import Header from './Header/Header';
import { filterVisibleColumns, renderPaginationTotal } from '../../../utils/render';
import { PAGINATION_SIZES } from '../../../utils/constants';

import styles from './Table.module.scss';
import BulkAction from './Actions/RowAction/BulkAction';

const sortDirectionNames = {
    default: '',
    ascend: 'ascend',
    descend: 'descend',
};

const summaryPlacements = {
    top: 'top',
    bottom: 'bottom',
};

const sortDirections = [sortDirectionNames.default, sortDirectionNames.ascend, sortDirectionNames.descend];

const Table = props => {
    const {
        data: dataSource,
        className,
        rowClassName: getRowClassName,
        paginationProps,
        rowKey: getRowKey,
        columns,
        summary,
        summaryPlacement = summaryPlacements.bottom,
        controlledSorting,
        showExpandAll,
        expandable: expandableProps,
        scroll,
        filterable,
        visibleColumns,
        loading,
        showColumnHeader,
        fixed,
        header,
        title,
        rowSelection,
        bulkActions,
        onChange,
        sortProps: controlledSortProps,
    } = props;

    const pagination = paginationProps
        ? {
              ...paginationProps,
              showTotal: renderPaginationTotal,
          }
        : false;

    const [expandedRows, setExpandedRows] = useState(expandableProps?.defaultExpandedRowKeys || []);
    const [checkedRows, setCheckedRows] = useState([]);
    const [isExpanded, setIsExpanded] = useState(false);
    const [internalSortProps, setInternalSortProps] = useState({
        key: sortDirectionNames.default,
        direction: sortDirectionNames.default,
    });

    const sortProps = controlledSortProps ?? internalSortProps;

    const [page, setPage] = useState({
        pageIndex: 1,
        pageSize: pagination.defaultPageSize,
    });

    const { pageIndex, pageSize } = page;

    const isControlled = !!expandableProps?.expandedRowKeys;
    const isExpandable = !!expandableProps;

    const expandable = expandableProps
        ? {
              ...expandableProps,
              expandedRowKeys: isControlled ? expandableProps.expandedRowKeys : expandedRows,
          }
        : {};

    const { onExpand, expandIcon } = expandable;

    useEffect(() => {
        if (dataSource.length) {
            const newExpandedRows = isExpanded
                ? dataSource.map(record => getRowKey(record))
                : dataSource.map(record => getRowKey(record)).filter(key => expandedRows.includes(key));

            setExpandedRows(newExpandedRows);
        }
    }, [dataSource.length]);

    useEffect(() => {
        setCheckedRows([]);
    }, [dataSource]);

    const onExpandToggle = useCallback(() => {
        const newExpandedRows = isExpanded ? [] : dataSource.map(record => getRowKey(record));

        setIsExpanded(!isExpanded);
        setExpandedRows(newExpandedRows);
    }, [isExpanded, dataSource, getRowKey]);

    const onRowExpand = (e, expanded, record) => {
        e.stopPropagation();

        if (!isExpandable) return;

        setExpandedRows(prevState => {
            const newExpandedRows = prevState.includes(getRowKey(record))
                ? prevState.filter(key => key !== getRowKey(record))
                : [...prevState, getRowKey(record)];

            if (newExpandedRows.length === dataSource.length) {
                setIsExpanded(true);
            } else setIsExpanded(false);

            return newExpandedRows;
        });

        if (onExpand) {
            onExpand(!expanded, record);
        }
    };

    const onSelect = (e, record) => {
        e.stopPropagation();

        setCheckedRows(prevState =>
            prevState.includes(record)
                ? prevState.filter(rowData => getRowKey(rowData) !== getRowKey(record))
                : [...prevState, record],
        );
    };

    const onSelectAll = e => {
        e.stopPropagation();

        setCheckedRows(e.target.checked ? data : []);
    };

    const expandAllColumn = useMemo(() => {
        return {
            key: 'expandAll',
            width: '1.5%',
            title: (
                <div onClick={onExpandToggle} className={'f-s-16 cursor-pointer p-2'}>
                    <DoubleRightOutlined rotate={isExpanded ? 270 : 90} />
                </div>
            ),
        };
    }, [isExpanded, onExpandToggle]);

    let tableColumns = useMemo(() => {
        return showExpandAll && !title ? columns.concat(expandAllColumn) : columns;
    }, [columns, isExpanded]);

    tableColumns = useMemo(() => {
        return filterable ? tableColumns.filter(column => filterVisibleColumns(visibleColumns, column)) : tableColumns;
    }, [tableColumns, visibleColumns]);

    tableColumns = useMemo(() => {
        return tableColumns.filter(v => !!v);
    }, [tableColumns]);

    const sortedData = useMemo(() => {
        if (controlledSorting) return dataSource;

        const sortedColumn = tableColumns.find(column => column.key === sortProps.key);
        const data = [...dataSource];

        if (sortedColumn) {
            data.sort((a, b) => {
                const value = sortedColumn.sorter(a, b);

                return sortProps.direction === sortDirectionNames.ascend ? value : value * -1;
            });
        }

        return data;
    }, [dataSource, sortProps]);

    const data = useMemo(() => {
        if (pagination) {
            const start = (pageIndex - 1) * pageSize;
            const end = pageIndex * pageSize;
            return sortedData.slice(start, end);
        } else {
            return sortedData;
        }
    }, [sortedData, page]);

    const onPaginationChange = (page, size) => {
        setPage({ pageIndex: page, pageSize: size });
    };

    const onSortClick = (key, sorter) => {
        const sortDirectionIndex = sortDirections.indexOf(internalSortProps.direction);

        let newSortDirectionIndex = sortDirectionIndex + 1;
        let newSortColumnKey = key;

        if (internalSortProps.key !== newSortColumnKey) {
            newSortDirectionIndex = 1;
        }

        if (sortDirectionIndex === sortDirections.length - 1) {
            newSortDirectionIndex = 0;
            newSortColumnKey = sortDirectionNames.default;
        }

        const newDirection = sortDirections[newSortDirectionIndex];

        setInternalSortProps({ key: newSortColumnKey, direction: newDirection });

        if (onChange !== undefined) {
            onChange(undefined, undefined, {
                direction: newDirection,
                key: newSortColumnKey,
            });
        }

        if (controlledSorting) {
            sorter({ order: newDirection, field: key });
        }
    };

    const renderSummary = () =>
        data.length && summary ? (
            <tr>
                {summary.map(cell => {
                    return (
                        <td key={cell.key} className={styles.summaryColumn}>
                            {cell.content}
                        </td>
                    );
                })}
            </tr>
        ) : null;

    return (
        <>
            <div className={classNames(styles.container, className)}>
                <div className={classNames(styles.spinnerContainer, { loading: loading })}>
                    <Spin />
                </div>
                {title && (
                    <Header
                        showExpandAll={showExpandAll}
                        onExpandToggle={onExpandToggle}
                        isExpanded={isExpanded}
                        title={title}
                    >
                        {header}
                    </Header>
                )}

                <div className={classNames(styles.tableContent, { horizontalScroll: !scroll.x })}>
                    <table className={classNames(styles.table, { fixedLayout: fixed })}>
                        {showColumnHeader && (
                            <ColumnsHeader
                                expandIcon={expandIcon}
                                tableColumns={tableColumns}
                                onSortClick={onSortClick}
                                sortProps={sortProps}
                                rowSelection={rowSelection}
                                onSelect={onSelectAll}
                                isAllSelected={data.length && checkedRows.length === data.length}
                            />
                        )}
                        {summaryPlacement === summaryPlacements.top && renderSummary()}
                        <Body
                            data={data}
                            expandable={expandable}
                            getRowClassName={getRowClassName}
                            getRowKey={getRowKey}
                            isExpandable={isExpandable}
                            onRowExpand={onRowExpand}
                            tableColumns={tableColumns}
                            rowSelection={rowSelection}
                            onSelect={onSelect}
                            checkedRows={checkedRows}
                        />
                        {summaryPlacement === summaryPlacements.bottom && renderSummary()}
                    </table>
                </div>
            </div>
            {checkedRows.length ? <BulkAction bulkActions={bulkActions} checkedRows={checkedRows} /> : null}
            {pagination && sortedData.length ? (
                <Pagination
                    className={styles.tablePagination}
                    total={sortedData.length}
                    pageSizeOptions={PAGINATION_SIZES}
                    onChange={(page, size) => onPaginationChange(page, size)}
                    {...pagination}
                />
            ) : null}
        </>
    );
};

Table.defaultProps = {
    filterable: false,
    controlledSorting: false,
    scroll: { x: true },
    showColumnHeader: true,
    rowSelection: false,
};

Table.Header = Header;

export default Table;
